/**
 * Pot-odds quiz: procedural math questions, no LLM grading.
 *
 * Given a pot of P and a villain bet of B, the equity hero needs to
 * profitably call is `B / (P + 2B)` — hero contributes B to a final
 * pot of P + 2B. This module owns the math + question generation; the
 * route renders + grades against it.
 *
 * Questions are multiple-choice with 4 options drawn from a canonical
 * set of equity values (matches sheet 5's defending-math table) — that
 * way the distractors are *meaningful* (they're answers to nearby bet
 * sizes the user might confuse) rather than random noise.
 */

/** Canonical bet sizes (as multiples of the pot) we pull questions
 *  from. Each lines up with a row in sheet 5's bluff-ratio table,
 *  so the reveal can name the canonical equity tier honestly. The
 *  pedagogical goal is *pattern recognition* — "ah, this is half-pot
 *  → 25%" — not arithmetic. So we constrain pot sizes per fraction
 *  to guarantee the bet always comes out a clean integer the user
 *  can recognize at a glance. */
interface BetTier {
  fraction: number;
  /** Pot sizes that produce an integer bet at this fraction. */
  cleanPots: readonly number[];
}

const BET_TIERS: readonly BetTier[] = [
  // "33% pot" → ~20% equity. Need pot divisible by 3.
  { fraction: 1 / 3, cleanPots: [12, 15, 18, 24, 30, 60, 90] },
  // "50% pot" → 25%. Any even pot.
  { fraction: 0.5, cleanPots: [10, 20, 30, 40, 50, 60, 80, 100] },
  // "66% pot" → ~29%. Need pot divisible by 3.
  { fraction: 2 / 3, cleanPots: [12, 15, 18, 30, 60, 90] },
  // "75% pot" → 30%. Need pot divisible by 4.
  { fraction: 0.75, cleanPots: [12, 16, 20, 40, 60, 80, 100] },
  // "Pot-sized" → ~33%. Any pot.
  { fraction: 1.0, cleanPots: [10, 20, 30, 40, 50, 60, 80, 100] },
  // "150% overbet" → ~38%. Any even pot.
  { fraction: 1.5, cleanPots: [10, 20, 30, 40, 60] },
  // "2x overbet" → 40%. Any pot.
  { fraction: 2.0, cleanPots: [10, 20, 30, 40, 50, 60] },
] as const;

/** Canonical equity values (in %) drawn from sheet 5. The correct
 *  answer is always one of these (we pick bet sizes that produce
 *  these exactly or near-exactly), and so are all distractors —
 *  meaningful confusions instead of random numbers. */
export const CANONICAL_EQUITIES = [20, 25, 29, 30, 33, 38, 40] as const;

export interface PotOddsQuestion {
  /** Pot size before villain's bet (bb). */
  pot: number;
  /** Villain's bet (bb). */
  bet: number;
  /** Bet expressed as a fraction of pot — used in the reveal label. */
  betFraction: number;
  /** The four options shown to the user, in display order. */
  options: number[];
  /** Index into `options` of the correct equity. */
  correctIndex: number;
}

/** Pure equity formula. Returns equity in percentage points (e.g. 25.0). */
export function equityNeeded(pot: number, bet: number): number {
  return (bet / (pot + 2 * bet)) * 100;
}

/** Snap a raw equity number to the nearest canonical value. Bet
 *  fractions like 1/3 produce 20.000…, 1/2 produce 25.000, but 2/3
 *  produces 28.571… — the snap normalizes those to the canonical
 *  rounded form (29) so the multiple-choice options line up with
 *  sheet 5's labels. */
function snapToCanonical(equity: number): number {
  let best: number = CANONICAL_EQUITIES[0]!;
  let bestDelta = Math.abs(equity - best);
  for (const v of CANONICAL_EQUITIES) {
    const delta = Math.abs(equity - v);
    if (delta < bestDelta) {
      best = v;
      bestDelta = delta;
    }
  }
  return best;
}

/** Build a fresh procedural question. Pure — accepts an optional RNG
 *  source for tests; defaults to Math.random in production. */
export function generateQuestion(
  rand: () => number = Math.random,
): PotOddsQuestion {
  const tier = BET_TIERS[Math.floor(rand() * BET_TIERS.length)]!;
  const pot = tier.cleanPots[Math.floor(rand() * tier.cleanPots.length)]!;
  const bet = pot * tier.fraction;
  // Pot sizes in BET_TIERS are picked so this is always an integer —
  // sanity check guards against future edits to the tier table.
  if (!Number.isInteger(bet)) {
    throw new Error(`Non-integer bet produced for tier ${tier.fraction} × pot ${pot}`);
  }
  const exactEquity = equityNeeded(pot, bet);
  const correct = snapToCanonical(exactEquity);
  const fraction = tier.fraction;

  // Build 3 distractors from the canonical set, near the correct
  // answer but not equal. Pick the two closest non-equal values plus
  // one farther one — the farther one prevents pure "pick the
  // middle" guessing.
  const sorted = [...CANONICAL_EQUITIES]
    .filter((v) => v !== correct)
    .sort((a, b) => Math.abs(a - correct) - Math.abs(b - correct));
  const distractors = [sorted[0]!, sorted[1]!, sorted[3]!]; // 2 near, 1 farther

  // Shuffle correct + distractors into the options array. Fisher-Yates.
  const options = [correct, ...distractors];
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [options[i], options[j]] = [options[j]!, options[i]!];
  }
  const correctIndex = options.indexOf(correct);

  return { pot, bet, betFraction: fraction, options, correctIndex };
}

/** Human-readable label for a bet's pot-fraction — used in the reveal
 *  to name the canonical sheet 5 tier. */
export function describeFraction(fraction: number): string {
  if (Math.abs(fraction - 1 / 3) < 0.05) return "33% pot bet";
  if (Math.abs(fraction - 0.5) < 0.05) return "50% pot bet (half-pot)";
  if (Math.abs(fraction - 2 / 3) < 0.05) return "66% pot bet (two-thirds)";
  if (Math.abs(fraction - 0.75) < 0.05) return "75% pot bet";
  if (Math.abs(fraction - 1.0) < 0.05) return "pot-sized bet";
  if (Math.abs(fraction - 1.5) < 0.1) return "150% pot overbet";
  if (Math.abs(fraction - 2.0) < 0.1) return "2x pot overbet";
  return `${Math.round(fraction * 100)}% pot bet`;
}

/** Format the equity-math line shown in the reveal. */
export function formulaLine(pot: number, bet: number, equity: number): string {
  const total = pot + 2 * bet;
  return `${bet} / (${pot} + 2×${bet}) = ${bet}/${total} ≈ ${equity}%`;
}
